<template>
  <div class="kv-ve__group">
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-image-url") }}</label>
      <div class="input-group">
        <input
          class="form-control"
          v-model="block.settings.src"
          :placeholder="$_('visualEditor-emailEditor-common-urlPlaceholder')"
        />
        <div class="input-group-append">
          <KvButton
            variant="outlined"
            prepend-icon="ui-file-search-03"
            :title="$_('visualEditor-emailEditor-image-chooseFile')"
            @click="filesModalOpen = true"
            style="border-radius: 0 8px 8px 0; height: 42px;"
          >
            {{ $_("visualEditor-emailEditor-image-chooseFile") }}
          </KvButton>
        </div>
      </div>
    </div>
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-image-alt") }}</label>
      <input class="form-control" v-model="block.settings.alt" />
    </div>
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-common-linkOptional") }}</label>
      <input
        class="form-control"
        v-model="block.settings.href"
        :placeholder="$_('visualEditor-emailEditor-common-urlPlaceholder')"
      />
    </div>
    <div class="form-group">
      <div style="display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:8px;">
        <div style="display:flex; align-items:baseline; gap:6px;">
          <label style="margin:0;">{{ $_("visualEditor-emailEditor-common-width") }}</label>
          <VeWidthUnitToggle
            v-model="widthModeProxy"
            :label="$_('visualEditor-emailEditor-common-width')"
            :pctTitle="$_('visualEditor-emailEditor-common-unitPercent')"
            :pxTitle="$_('visualEditor-emailEditor-common-unitPx')"
          />
        </div>
        <div style="display:flex; align-items:center; gap:8px;">
          <input
            v-if="widthModeProxy === 'pct'"
            class="form-control"
            type="number"
            v-model.number="widthPctProxy"
            min="10"
            max="100"
            style="width: 88px; height: 34px; padding: 6px 10px; font-size: 12px; font-variant-numeric: tabular-nums;"
          />
          <input
            v-else
            class="form-control"
            type="number"
            v-model.number="widthPxProxy"
            min="0"
            max="600"
            style="width: 88px; height: 34px; padding: 6px 10px; font-size: 12px; font-variant-numeric: tabular-nums;"
          />
          <div style="font-size:12px; font-variant-numeric: tabular-nums; color:#667085;">
            {{ widthModeProxy === "pct" ? $_("visualEditor-emailEditor-common-unitPercent") : $_("visualEditor-emailEditor-common-unitPx") }}
          </div>
        </div>
      </div>
      <VeWidthSlider
        v-if="widthModeProxy === 'pct'"
        v-model="widthPctProxy"
        :label="$_('visualEditor-emailEditor-common-width')"
        :min="10"
        :max="100"
        :step="1"
        unit="%"
        :showHeader="false"
      />
      <VeWidthSlider
        v-else
        v-model="widthPxProxy"
        :label="$_('visualEditor-emailEditor-common-widthPx')"
        :min="0"
        :max="600"
        :step="10"
        unit="px"
        :showHeader="false"
      />
    </div>
    <div class="form-row">
      <div class="form-group col-6">
        <label>{{ $_("visualEditor-emailEditor-image-display") }}</label>
        <VeDisplayModePicker
          v-model="displayProxy"
          :label="$_('visualEditor-emailEditor-image-display')"
          :blockTitle="$_('visualEditor-emailEditor-image-displayBlock')"
          :inlineTitle="$_('visualEditor-emailEditor-image-displayInline')"
        />
      </div>
    </div>
    <div class="form-group" v-if="displayProxy === 'block'">
      <label>{{ $_("visualEditor-emailEditor-common-align") }}</label>
      <VeAlignPicker
        v-model="alignProxy"
        :label="$_('visualEditor-emailEditor-common-align')"
        :leftTitle="$_('visualEditor-emailEditor-common-left')"
        :centerTitle="$_('visualEditor-emailEditor-common-center')"
        :rightTitle="$_('visualEditor-emailEditor-common-right')"
        :allowEmpty="true"
      />
    </div>
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-common-corners") }}</label>
      <VeCornersPicker
        v-model="block.settings.borderRadius"
        :options="borderRadiusOptions.map((o) => ({ value: o.value, title: $_(o.labelKey) }))"
      />
    </div>
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-image-aspectRatio") }}</label>
      <VeAspectRatioPicker
        v-model="aspectRatioProxy"
        :label="$_('visualEditor-emailEditor-image-aspectRatio')"
        :options="aspectRatioOptions"
      />
    </div>

    <VeBlockMarginsSettings :block="block" />

    <FilesGalleryModal
      v-model="filesModalOpen"
      size="large"
      :selectable="true"
      :autoCloseOnSelect="true"
      :allowUpload="true"
      :allowDelete="false"
      type-filter="image"
      @select="onPickFile"
    />
  </div>
</template>

<script>
import KvButton from "@/components/form/kv-button.vue";
import FilesGalleryModal from "@/files-gallery/files-gallery-modal.vue";
import VeCornersPicker from "../../components/VeCornersPicker.vue";
import VeWidthSlider from "../../components/VeWidthSlider.vue";
import VeAlignPicker from "../../components/VeAlignPicker.vue";
import VeDisplayModePicker from "../../components/VeDisplayModePicker.vue";
import VeAspectRatioPicker from "../../components/VeAspectRatioPicker.vue";
import VeWidthUnitToggle from "../../components/VeWidthUnitToggle.vue";
import VeBlockMarginsSettings from "../VeBlockMarginsSettings.vue";

export default {
  name: "VeImageBlockSettings",
  components: { KvButton, FilesGalleryModal, VeCornersPicker, VeWidthSlider, VeAlignPicker, VeDisplayModePicker, VeAspectRatioPicker, VeWidthUnitToggle, VeBlockMarginsSettings },
  props: {
    block: { type: Object, required: true },
    paletteLabel: { type: String, required: true },
    borderRadiusOptions: { type: Array, required: true },
  },
  data() {
    return {
      filesModalOpen: false,
    };
  },
  computed: {
    widthModeProxy: {
      get() {
        const v = String(this.block?.settings?.widthMode || "").toLowerCase();
        if (v === "px" || v === "pct") return v;
        // Default: % for block, px for inline-block
        return this.displayProxy === "inline-block" ? "px" : "pct";
      },
      set(next) {
        const v = String(next || "").toLowerCase();
        const safe = v === "px" ? "px" : "pct";
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "widthMode", safe);
      },
    },
    aspectRatioOptions() {
      // Display only the ratio notation in the UI, while keeping stored values the same.
      return [
        { value: "auto", label: this.$_("visualEditor-emailEditor-image-aspectRatioAuto"), title: this.$_("visualEditor-emailEditor-image-aspectRatioAuto") },
        { value: "1 / 1", label: "1:1" },
        { value: "16 / 9", label: "16:9" },
        { value: "9 / 16", label: "9:16" },
        { value: "4 / 3", label: "4:3" },
        { value: "3 / 4", label: "3:4" },
        { value: "3 / 2", label: "3:2" },
        { value: "2 / 3", label: "2:3" },
      ];
    },
    aspectRatioProxy: {
      get() {
        const v = String(this.block?.settings?.aspectRatio || "");
        const allowed = new Set(["auto", "1 / 1", "16 / 9", "9 / 16", "4 / 3", "3 / 4", "3 / 2", "2 / 3"]);
        return allowed.has(v) ? v : "auto";
      },
      set(next) {
        const v = String(next || "");
        const allowed = new Set(["auto", "1 / 1", "16 / 9", "9 / 16", "4 / 3", "3 / 4", "3 / 2", "2 / 3"]);
        const safe = allowed.has(v) ? v : "auto";
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "aspectRatio", safe);
      },
    },
    displayProxy: {
      get() {
        const v = String(this.block?.settings?.display || "").toLowerCase();
        // Back-compat: "inline" -> "inline-block"
        return v === "inline-block" || v === "inline" ? "inline-block" : "block";
      },
      set(next) {
        const v = String(next || "").toLowerCase();
        const safe = v === "inline-block" || v === "inline" ? "inline-block" : "block";
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "display", safe);
      },
    },
    alignProxy: {
      get() {
        const v = String(this.block?.settings?.align || "").toLowerCase();
        return v === "left" || v === "center" || v === "right" ? v : null;
      },
      set(next) {
        if (!this.block.settings) this.$set(this.block, "settings", {});
        const v = String(next || "").toLowerCase();
        const safe = v === "left" || v === "center" || v === "right" ? v : null;
        if (!safe) this.$delete(this.block.settings, "align");
        else this.$set(this.block.settings, "align", safe);
      },
    },
    widthPctProxy: {
      get() {
        const n = Number(this.block?.settings?.widthPct);
        if (!Number.isFinite(n)) return 100;
        return Math.min(100, Math.max(10, Math.round(n)));
      },
      set(v) {
        const n = Number(v);
        const next = Number.isFinite(n) ? Math.min(100, Math.max(10, Math.round(n))) : 100;
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "widthPct", next);
      },
    },
    widthPxProxy: {
      // 0 means "auto" (no fixed px constraint)
      get() {
        const n = Number(this.block?.settings?.widthPx);
        if (!Number.isFinite(n)) return 0;
        return Math.min(600, Math.max(0, Math.round(n)));
      },
      set(v) {
        const n = Number(v);
        const next = Number.isFinite(n) ? Math.min(600, Math.max(0, Math.round(n))) : 0;
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "widthPx", next);
      },
    },
  },
  methods: {
    onPickFile(file) {
      if (!file || !file.url) return;
      this.block.settings.src = file.url;
      this.filesModalOpen = false;
    },
  },
};
</script>


